import { FileWithShare } from "@/lib/supabase/supabaseTypes";
import { FileIconComponent } from "@/dentlab/src/components/file-icon/file-icon.component";
import { getFileTypeWithDocumentType } from "@/lib/utils/file-utils";
import { JobDocumentWithFiles } from "@/dentlab/src/store/Jobs/job-documents.store";
import { useFileActions } from "@/dentlab/src/hooks/useFileActions";
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from "@/dentlab/src/components/ui/hover-card";
import { ShareFileButton } from "@/dentlab/src/components/sharing/share-button/share-file-button.component";
import { UnshareDialog } from "@/dentlab/src/components/sharing/unshare-dialog/unshare-dialog.component";
import { InvoiceCancellationDialog } from "@/dentlab/src/pages/debitoren/components/invoice-cancellation-dialog.component";
import clsx from "clsx";

interface JobDocumentSidebarFileItemProps {
	file: FileWithShare;
	jobDocument: JobDocumentWithFiles;
	onOpenFile: (fileId: string) => void;
}

export const JobDocumentSidebarFileItem: React.FC<JobDocumentSidebarFileItemProps> = ({
	file,
	jobDocument,
	onOpenFile,
}) => {
	const {
		openShareDialog,
		setOpenShareDialog,
		openUnshareDialog,
		setOpenUnshareDialog,
		openCancelInvoiceDialog,
		cancellationLoading,
		invoiceData,
		handleShare,
		handleUnshare,
		handleDelete,
		handleDownload,
		fetchInvoicePaymentData,
		handleCancelInvoice,
		handleCancelInvoiceDialogClose,
		canShare,
		canDelete,
		canCancelInvoice,
		isFileShared,
		wasFileShared,
		shareColor,
	} = useFileActions({ file, jobDocument });

	const fileType = getFileTypeWithDocumentType(
		(file?.meta_data || null) as Record<string, any> | null,
		file.file_name
	);

	return (
		<>
			<HoverCard openDelay={200} closeDelay={100}>
				<HoverCardTrigger asChild>
					<button
						onClick={() => file.id && onOpenFile(file.id)}
						className={clsx(
							"flex items-center gap-1.5 pl-6 pr-2 py-0.5 w-full text-left hover:bg-gray-100 rounded-sm group",
							file.invoice_canceled_at && "opacity-60"
						)}
						data-testid={`job-document-sidebar-file-${file.id}`}
						aria-label={`Datei ${file.file_name} öffnen`}
					>
						<FileIconComponent fileType={fileType} size={14} color={shareColor} />
						<span
							className={clsx(
								"text-xs text-gray-600 truncate flex-1",
								file.invoice_canceled_at && "line-through text-red-600"
							)}
						>
							{file.file_name}
						</span>
					</button>
				</HoverCardTrigger>
				<HoverCardContent className="w-auto p-1" sideOffset={5} side="right">
					<div className="flex flex-col">
						<button
							onClick={(e) => {
								e.stopPropagation();
								handleDownload();
							}}
							className="flex justify-between items-center cursor-pointer hover:bg-neutral-100 px-3 py-1.5 text-sm rounded-sm text-left"
							data-testid="sidebar-file-download"
						>
							Herunterladen
						</button>
						{canShare && (
							<button
								onClick={(e) => {
									e.stopPropagation();
									if (isFileShared) {
										setOpenUnshareDialog(true);
									} else {
										setOpenShareDialog(true);
									}
								}}
								className="flex justify-between items-center cursor-pointer hover:bg-neutral-100 px-3 py-1.5 text-sm rounded-sm text-left"
								data-testid="sidebar-file-share"
							>
								{isFileShared
									? "Teilen aufheben"
									: wasFileShared
										? "Erneut Teilen"
										: "Teilen"}
							</button>
						)}
						{canCancelInvoice && (
							<button
								onClick={(e) => {
									e.stopPropagation();
									fetchInvoicePaymentData();
								}}
								className="flex justify-between items-center cursor-pointer hover:bg-neutral-100 px-3 py-1.5 text-sm rounded-sm text-left"
								data-testid="sidebar-file-cancel-invoice"
							>
								Stornieren
							</button>
						)}
						{canDelete && (
							<button
								onClick={(e) => {
									e.stopPropagation();
									handleDelete();
								}}
								className="flex justify-between items-center cursor-pointer hover:bg-neutral-100 px-3 py-1.5 text-sm rounded-sm text-left"
								data-testid="sidebar-file-delete"
							>
								In den Papierkorb legen
							</button>
						)}
					</div>
				</HoverCardContent>
			</HoverCard>

			{openShareDialog && (
				<ShareFileButton
					open={openShareDialog}
					setOpen={setOpenShareDialog}
					file={file}
					handleShare={handleShare}
				/>
			)}
			{openUnshareDialog && (
				<UnshareDialog
					open={openUnshareDialog}
					onClose={() => setOpenUnshareDialog(false)}
					shared={isFileShared}
					entityName={file.file_name}
					onUnshare={handleUnshare}
				/>
			)}
			{openCancelInvoiceDialog && (
				<InvoiceCancellationDialog
					isOpen={openCancelInvoiceDialog}
					onClose={handleCancelInvoiceDialogClose}
					onConfirm={handleCancelInvoice}
					loading={cancellationLoading}
					invoice={invoiceData}
				/>
			)}
		</>
	);
};
